/**
 * La pile du valideur : ce qui attend MA validation, tous chantiers confondus.
 *
 * *Demande de Chris, 17/09/2026 : « quand le responsable a saisi et validé les heures + confirmé
 * les réceptions transport, le circuit de validation passe par le conducteur du chantier qui peut
 * modifier les saisies et qui doit valider ensuite ; cela doit aller à l'assistante référente, qui
 * doit aussi pouvoir modifier et valider ; et ensuite tout arrive à la responsable RH. »*
 *
 * ## Pourquoi un écran à part, et pas un onglet de la saisie
 *
 * Le chef part de SA journée : une date, ses chantiers. Le conducteur, l'assistante et la RH
 * partent de **leur pile** — ils ne savent pas d'avance quel chantier leur a passé quoi, ni à
 * quelle date. Leur faire chercher chantier par chantier, jour par jour, aurait garanti qu'une
 * journée reste en attente sans que personne s'en aperçoive.
 *
 * C'est le même raisonnement que les soumissions VAL-CE, et c'est aussi pour cela que les trois
 * rôles partagent un seul écran : le geste est identique, et ce qui change — quelle étape
 * j'occupe — se déduit du compte. Trois entrées de menu auraient obligé chacun à connaître son
 * titre avant de travailler.
 *
 * ## Modifier avant de valider passe par l'écran de saisie
 *
 * *« Le conducteur du chantier qui peut modifier les saisies. »* Le bouton « Ouvrir la journée »
 * envoie donc sur l'écran de saisie, sur la bonne date et le bon chantier — un seul écran de
 * saisie, un seul chemin d'écriture. Deux écrans qui écriraient les mêmes heures auraient fini
 * par se contredire, la panne du 16/08 à l'échelle du produit.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { formaterDate, formaterNombre } from "@planif/moteur";
import { chargerCeQuiAttendMaValidation, validerLaJournee } from "./api";
import type { JourneeAValider } from "./api";

/**
 * D'où vient la journée, en toutes lettres.
 *
 * Le statut dit jusqu'où elle est montée ; le valideur, lui, veut savoir **qui la lui passe**.
 * « Close » ne le dit pas — c'est le vocabulaire du chef, pas celui du conducteur.
 */
function direLaProvenance(statut: JourneeAValider["statut"]): string {
  if (statut === "close") return "déclarée par le chantier";
  if (statut === "validee_conducteur") return "validée par le conducteur";
  if (statut === "validee_assistante") return "validée par l'assistante référente";
  return "";
}

export function ValidationHeures({
  ouvrirLaJournee,
}: {
  /** Ouvre l'écran de saisie sur cette journée, pour la corriger avant de valider. */
  readonly ouvrirLaJournee?: (cible: { chantierId: string; jour: string }) => void;
}): JSX.Element {
  const [pile, setPile] = useState<readonly JourneeAValider[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [commentaires, setCommentaires] = useState<Map<string, string>>(new Map());

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const lu = await chargerCeQuiAttendMaValidation();
      setPile(lu.aValider);
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const valider = async (journee: JourneeAValider): Promise<void> => {
    setEnCours(journee.journeeId);
    setErreur(null);
    setMessage(null);
    try {
      await validerLaJournee({
        chantierId: journee.chantierId,
        jour: journee.jour,
        commentaire: commentaires.get(journee.journeeId) ?? null,
      });
      setMessage(
        `Journée du ${formaterDate(journee.jour)} sur ${journee.apo} validée : elle poursuit son circuit.`,
      );
      await charger();
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setEnCours(null);
    }
  };

  return (
    <section className="validation-heures">
      <header className="validation-heures__entete">
        <h2>Validation des heures</h2>
        <p className="validation-heures__aide">
          Les journées déclarées par les chantiers qui attendent votre validation. Ouvrez-en une
          pour corriger les heures, les paniers ou les livraisons avant de la faire monter.
        </p>
        <button type="button" onClick={() => { void charger(); }} disabled={chargement}>
          Rafraîchir
        </button>
      </header>

      {erreur !== null && <p className="erreur">{erreur}</p>}
      {message !== null && <p className="message">{message}</p>}

      {chargement && <p>Chargement…</p>}

      {!chargement && pile.length === 0 && (
        <p className="validation-heures__vide">
          Rien n'attend votre validation. Les journées apparaissent ici dès que le responsable du
          chantier les a déclarées, et repartent dès que vous les avez validées.
        </p>
      )}

      {pile.map((journee) => (
        <article key={journee.journeeId} className="validation-heures__journee">
          <header>
            <h3>
              {journee.apo} — {journee.nom}
            </h3>
            <p className="validation-heures__jour">
              {formaterDate(journee.jour)} · {direLaProvenance(journee.statut)}
            </p>
          </header>

          <dl className="validation-heures__chiffres">
            <div>
              <dt>Heures déclarées</dt>
              {/* Un total, pas une addition à faire de tête : c'est le chiffre qui décide si la
                  journée mérite d'être ouverte avant d'être validée. */}
              <dd>{formaterNombre(journee.heures, 2)} h</dd>
            </div>
            <div>
              <dt>Personnes</dt>
              <dd>{journee.personnes}</dd>
            </div>
            <div>
              <dt>Votre étape</dt>
              <dd>{journee.libelleEtape}</dd>
            </div>
          </dl>

          <label className="validation-heures__commentaire">
            Commentaire (facultatif)
            <input
              type="text"
              value={commentaires.get(journee.journeeId) ?? ""}
              placeholder="Ce que la suite du circuit doit savoir"
              onChange={(evenement) => {
                const valeur = evenement.target.value;
                setCommentaires((precedent) => {
                  const copie = new Map(precedent);
                  copie.set(journee.journeeId, valeur);
                  return copie;
                });
              }}
            />
          </label>

          <div className="validation-heures__gestes">
            {ouvrirLaJournee !== undefined && (
              <button
                type="button"
                onClick={() => {
                  ouvrirLaJournee({ chantierId: journee.chantierId, jour: journee.jour });
                }}
              >
                Ouvrir la journée
              </button>
            )}
            <button
              type="button"
              className="primaire"
              disabled={enCours !== null}
              onClick={() => {
                void valider(journee);
              }}
            >
              {enCours === journee.journeeId ? "Validation…" : "Valider"}
            </button>
          </div>
        </article>
      ))}
    </section>
  );
}
